import { CollaborationEdge } from '../../../types/definition';

interface EdgePropertiesProps {
  edge: CollaborationEdge;
  sourceNodeName?: string;
  targetNodeName?: string;
  onChange: (updates: Partial<CollaborationEdge>) => void;
  /**
   * Inspect, do not edit (task 881). An edge's condition is the thing a reader
   * most often clicks it for, so it still reads — disabled, with no change
   * handler attached.
   */
  readOnly?: boolean;
}

export function EdgeProperties({ edge, sourceNodeName, targetNodeName, onChange, readOnly = false }: EdgePropertiesProps) {
  return (
    <div className="space-y-4" aria-readonly={readOnly || undefined}>
      {/* Connection Info (read-only) */}
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Connection</div>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {sourceNodeName || edge.from}
          </span>
          <span className="text-gray-400">→</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {targetNodeName || edge.to}
          </span>
        </div>
      </div>

      {/* Label */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Label (optional)
        </label>
        <input
          type="text"
          value={edge.label || ''}
          onChange={readOnly ? undefined : (e) => onChange({ label: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="Edge label"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-indigo-500 focus:ring-indigo-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Displayed on the edge in the diagram
        </p>
      </div>

      {/* Condition */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Condition (optional)
        </label>
        <input
          type="text"
          value={edge.condition || ''}
          onChange={readOnly ? undefined : (e) => onChange({ condition: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="{{nodes.previous.success}} === true"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-indigo-500 focus:ring-indigo-500
                     dark:bg-gray-700 dark:text-white text-sm font-mono"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Edge is only followed when condition evaluates to true
        </p>
      </div>

      {/* Edge ID (read-only) */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Edge ID
        </label>
        <div className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate">
          {edge.id || 'auto-generated'}
        </div>
      </div>
    </div>
  );
}
